/**
 * Settings > Logging — UC poll audit (D1 uc_poll_log). Super Admin only.
 */
import { useState, useEffect, useCallback, type CSSProperties } from 'react';
import { useAuth } from '@clerk/clerk-react';

type PollRow = {
  id: number;
  created_at: string;
  ok: boolean;
  duration_ms: number | null;
  http_status: number | null;
  reason: string | null;
  agent_count: number | null;
  source: string;
};

const POLL_REASON_LABELS: Record<string, string> = {
  ipscape_disabled: 'Polling disabled in settings (no UC request)',
  outside_operating_hours: 'Outside operating hours (no UC request)',
  uc_credentials: 'UC credentials missing or invalid',
  uc_unauthorized: 'UC token rejected (401)',
  agent_state_http: 'UC agent-state HTTP error',
  invalid_json: 'Invalid UC response',
  no_database: 'Database unavailable',
  empty_agent_list: 'UC returned no agents',
};

function formatPollReason(reason: string | null): string {
  if (!reason) return '—';
  return POLL_REASON_LABELS[reason] ?? reason;
}

function formatLocal(isoOrSql: string): string {
  const d = new Date(isoOrSql.includes('T') ? isoOrSql : `${isoOrSql.replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return isoOrSql;
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' });
}

export default function SettingsLogging() {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<PollRow[]>([]);
  const [total, setTotal] = useState(0);
  const [retentionDays, setRetentionDays] = useState(1);
  const [savingRetention, setSavingRetention] = useState(false);
  const [retentionMsg, setRetentionMsg] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const apiBase = typeof window !== 'undefined' ? window.location.origin : '';

  const loadLogs = useCallback(async () => {
    const token = await getToken();
    if (!token) throw new Error('Not signed in');
    const res = await fetch(`${apiBase}/api/poll-log?limit=200`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = (await res.json().catch(() => ({}))) as {
      error?: string;
      rows?: PollRow[];
      total?: number;
    };
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    setRows(Array.isArray(data.rows) ? data.rows : []);
    setTotal(typeof data.total === 'number' ? data.total : 0);
  }, [getToken, apiBase]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) {
        if (!cancelled) setLoading(false);
        return;
      }
      setError(null);
      try {
        const [logRes, cfgRes] = await Promise.all([
          fetch(`${apiBase}/api/poll-log?limit=200`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${apiBase}/api/config?keys=uc_poll_log_retention_days`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const logData = (await logRes.json().catch(() => ({}))) as { error?: string; rows?: PollRow[]; total?: number };
        if (!logRes.ok) {
          throw new Error(logData.error || `HTTP ${logRes.status}`);
        }
        if (!cancelled) {
          setRows(Array.isArray(logData.rows) ? logData.rows : []);
          setTotal(typeof logData.total === 'number' ? logData.total : 0);
        }
        if (cfgRes.ok) {
          const cfg = (await cfgRes.json()) as { uc_poll_log_retention_days?: string };
          const d = parseInt(cfg.uc_poll_log_retention_days ?? '1', 10);
          if (!cancelled && d >= 1 && d <= 30) setRetentionDays(d);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, apiBase]);

  const saveRetention = async () => {
    const token = await getToken();
    if (!token) return;
    setSavingRetention(true);
    setRetentionMsg(null);
    try {
      const res = await fetch(`${apiBase}/api/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ uc_poll_log_retention_days: retentionDays }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setRetentionMsg('Saved.');
    } catch (e) {
      setRetentionMsg(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavingRetention(false);
    }
  };

  const thStyle: CSSProperties = {
    padding: '0.45rem 0.5rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--text-muted)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    whiteSpace: 'nowrap',
  };

  if (loading) {
    return <p style={{ color: 'var(--text-muted)' }}>Loading…</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <h2 style={{ margin: 0, fontSize: '1.125rem' }}>Logging</h2>
      <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-muted)', maxWidth: 640 }}>
        Log entries are written when the worker actually attempts UC <code style={{ fontSize: '0.8em' }}>GET /agent-state</code>{' '}
        (or manual directory refresh). Rows with reason &quot;Polling disabled&quot; are no longer logged every few
        seconds while ipSCAPE is off. No request or response bodies are stored. Super Admin only.
      </p>

      {error ? (
        <p style={{ color: '#f87171', margin: 0 }}>{error}</p>
      ) : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
        <label style={{ fontSize: '0.875rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Log retention (days)
          <input
            type="number"
            min={1}
            max={30}
            value={retentionDays}
            onChange={(e) => setRetentionDays(Math.min(30, Math.max(1, parseInt(e.target.value, 10) || 1)))}
            style={{
              width: 64,
              padding: '0.35rem 0.5rem',
              borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.12)',
              background: 'var(--bg)',
              color: 'var(--text)',
            }}
          />
        </label>
        <button
          type="button"
          onClick={saveRetention}
          disabled={savingRetention}
          style={{
            padding: '0.45rem 1rem',
            background: 'var(--accent)',
            border: 'none',
            borderRadius: 6,
            color: 'var(--text)',
            cursor: savingRetention ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
          }}
        >
          {savingRetention ? 'Saving…' : 'Save retention'}
        </button>
        <button
          type="button"
          onClick={() => {
            setRefreshing(true);
            setError(null);
            loadLogs()
              .catch((e) => setError(e instanceof Error ? e.message : 'Refresh failed'))
              .finally(() => setRefreshing(false));
          }}
          disabled={refreshing}
          style={{
            padding: '0.45rem 1rem',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 6,
            color: 'var(--text)',
            cursor: refreshing ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
          }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh table'}
        </button>
        {retentionMsg ? <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{retentionMsg}</span> : null}
      </div>

      <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
        Showing {rows.length} of {total} rows (latest first).
      </p>

      <div style={{ overflowX: 'auto', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
          <thead>
            <tr>
              <th style={thStyle}>Time</th>
              <th style={thStyle}>OK</th>
              <th style={thStyle}>Duration (ms)</th>
              <th style={thStyle}>HTTP</th>
              <th style={thStyle}>Agents</th>
              <th style={thStyle}>Source</th>
              <th style={thStyle}>Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '1rem', color: 'var(--text-muted)' }}>
                  No poll rows yet. Enable UC polling and operating hours, or run a test sync from Integrations.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    {formatLocal(r.created_at)}
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    {r.ok ? 'Yes' : 'No'}
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    {r.duration_ms != null ? r.duration_ms : '—'}
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    {r.http_status != null ? r.http_status : '—'}
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    {r.agent_count != null ? r.agent_count : '—'}
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    {r.source}
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)', maxWidth: 320 }}>
                    {formatPollReason(r.reason)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
